import React, { useState, useEffect } from 'react';
import Select from "react-select";
import { Modal, Button, Container, Row, Col } from 'react-bootstrap';
import Swal from 'sweetalert2';
import RequestServies from '../../../services/RequestServies';
import PoServices from "../../../services/PoServices";

const AddMrvForm = ({ show, handleClose, store, onSuccess }) => {
  const initialFormValues = {
    store: '',
    supplier: '',
    billingTitle: '',  // ✅ new field
    billingNo: '',
    billingDate: '',
    image: null,
    itemCount: '',
    otherCharges: '',
    totalAmount: ''
  };

  const [formValues, setFormValues] = useState(initialFormValues);
  const [previewImage, setPreviewImage] = useState('img/placeholder-img.png');
  const [suppliers, setSuppliers] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isCompressing, setIsCompressing] = useState(false);

  // Fetch suppliers when modal opens
  useEffect(() => {
    if (show) {
      PoServices.activeSuppliers()
        .then((res) => {
          const sortedSuppliers = (res.data || []).sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
          );
          setSuppliers(sortedSuppliers);
        })
        .catch((err) => {
          console.error("Failed to load suppliers", err);
          Swal.fire("Error", "Failed to fetch suppliers", "error");
        });
    }
  }, [show]);



  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleFileChange = async (event) => {
    const files = event.target.files;

    if (!files || files.length === 0) return;

    const file = files[0];

    // ✅ SHOW PREVIEW FIRST (fix)
    const previewURL = URL.createObjectURL(file);
    setPreviewImage(previewURL);

    setIsCompressing(true);

    try {
      const img = new Image();
      img.src = previewURL;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 800;

        const scaleSize = MAX_WIDTH / img.width;
        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scaleSize;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          setIsCompressing(false);

          if (!blob) {
            // ✅ fallback
            setFormValues({ ...formValues, image: file });
            return;
          }

          const compressedFile = new File([blob], file.name, {
            type: 'image/jpeg',
          });

          setFormValues({
            ...formValues,
            image: compressedFile,
          });

        }, 'image/jpeg', 0.7);
      };

      img.onerror = () => {
        setIsCompressing(false);
        setFormValues({ ...formValues, image: file });
      };

    } catch (err) {
      setIsCompressing(false);
      setFormValues({ ...formValues, image: file });
    }
  };

  const validateForm = () => {
    if (!formValues.store) {
      Swal.fire("Warning", "Please select a store.", "warning");
      return false;
    }
    if (!formValues.supplier) {
      Swal.fire("Warning", "Please select a supplier.", "warning");
      return false;
    }
    if (!formValues.billingNo) {
      Swal.fire("Warning", "Please enter the bill number.", "warning");
      return false;
    }
    if (!formValues.totalAmount || formValues.totalAmount < 0) {
      Swal.fire("Warning", "Please enter a valid total amount.", "warning");
      return false;
    }
    if (!formValues.billingDate) {
      Swal.fire("Warning", "Please select the bill date.", "warning");
      return false;
    }
    if (!formValues.itemCount || formValues.itemCount <= 0) {
      Swal.fire("Warning", "Please enter a valid item count.", "warning");
      return false;
    }
    if (!formValues.otherCharges || formValues.otherCharges < 0) {
      Swal.fire("Warning", "Please enter valid other bill charges.", "warning");
      return false;
    }
    if (!formValues.image) {
      Swal.fire("Warning", "Please upload an image.", "warning");
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setUploadProgress(0); // ✅ reset before upload

    try {
      const formData = new FormData();

      Object.keys(formValues).forEach((key) => {
        if (key === 'image' && formValues.image) {
          formData.append('image', formValues.image);
        } else {
          formData.append(key, formValues[key]);
        }
      });

      await RequestServies.createMrv(formData, (progressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setUploadProgress(percent);
      });

      Swal.fire("Success", "MRV form added successfully!", "success");

      setUploadProgress(0); // ✅ reset after success

      handleClose();
      onSuccess();

    } catch (error) {

      setUploadProgress(0); // ✅ reset on error (optional but better)

      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to add MRV!",
        "error"
      );
    }
  };
  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setFormValues({
        store: '',
        supplier: '',
        billingTitle: '',
        billingNo: '',
        billingDate: '',
        image: null,
        itemCount: '',
        otherCharges: '',
        totalAmount: ''
      });
      setPreviewImage('img/placeholder-img.png');
    }
  }, [show]);

  return (


    <Modal className="fade" show={show} onHide={handleClose}>
      <form onSubmit={handleSubmit}>
        <Modal.Header>
          <Modal.Title>
            <i className="fas fa-edit"></i> MRV Form
          </Modal.Title>
          <Button variant="" className="btn-close" onClick={handleClose}></Button>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Row>
              {/* Store Dropdown */}
              <Col md="12">
                <label>Store</label>
                <Select
                  options={store.map((s) => ({
                    value: s._id,
                    label: s.name
                  }))}
                  value={
                    store
                      .map((s) => ({ value: s._id, label: s.name }))
                      .find((opt) => opt.value === formValues.store) || null
                  }
                  onChange={(selected) =>
                    setFormValues({
                      ...formValues,
                      store: selected ? selected.value : ""
                    })
                  }
                  placeholder="Search Store..."
                  isClearable
                />
              </Col>

              {/* Supplier Dropdown */}
              <Col md="12 mt-3">
                <label>Supplier</label>
                <Select
                  options={suppliers.map((sup) => ({
                    value: sup._id,
                    label: `${sup?.name} - ${sup?.address?.state || ""}`
                  }))}
                  value={
                    suppliers
                      .map((sup) => ({
                        value: sup._id,
                        label: `${sup?.name} - ${sup?.address?.state || ""}`
                      }))
                      .find((opt) => opt.value === formValues.supplier) || null
                  }
                  onChange={(selected) =>
                    setFormValues({
                      ...formValues,
                      supplier: selected ? selected.value : ""
                    })
                  }
                  placeholder="Search Supplier..."
                  isClearable
                />
              </Col>

           
              {/* Bill Title */}
              <Col md="6 mt-3">
                <label>Bill Title (e.g., RCT/25)</label>
                <input
                  type="text"
                  name="billingTitle"
                  value={formValues.billingTitle}
                  onChange={handleInputChange}
                  className="form-control form-control-sm"
                  placeholder="Enter Bill Title (optional)"
                />
              </Col>


              {/* Bill Number */}
              <Col md="6 mt-3">
                <label>Bill Number</label>
                <input
                  type="number"
                  name="billingNo"
                  value={formValues.billingNo}
                  onChange={handleInputChange}
                  className="form-control form-control-sm"
                />
              </Col>

              {/* Total Amount */}
              <Col md="6 mt-3">
                <label>Total Amount</label>
                <input
                  type="number"
                  name="totalAmount"
                  value={formValues.totalAmount}
                  onChange={handleInputChange}
                  className="form-control form-control-sm"
                />
              </Col>

              {/* Other Bill Charges */}
              <Col md="6 mt-3">
                <label>Other Bill Charges</label>
                <input
                  type="number"
                  name="otherCharges"
                  value={formValues.otherCharges}
                  onChange={handleInputChange}
                  className="form-control form-control-sm"
                />
              </Col>

              {/* Bill Date */}
              <Col md="6 mt-3">
                <label>Bill Date</label>
                <input
                  type="date"
                  name="billingDate"
                  value={formValues.billingDate}
                  onChange={handleInputChange}
                  className="form-control form-control-sm"
                />
              </Col>

              {/* Item Count */}
              <Col md="6 mt-3">
                <label>Item Count</label>
                <input
                  type="number"
                  name="itemCount"
                  value={formValues.itemCount}
                  onChange={handleInputChange}
                  className="form-control form-control-sm"
                />
              </Col>

              {/* Image Upload */}
              <Col md="12" className="mt-3">
                <label>Upload Bill Image</label>

                <input
                  type="file"
                  className="form-control form-control-sm"
                  accept="image/*"
                  capture="environment"
                  onClick={(e) => {
                    e.target.value = null;
                  }}
                  onChange={handleFileChange}
                />

                {/* ✅ Compression Loader */}
                {isCompressing && (
                  <div className="text-center mt-2">
                    <div className="spinner-border text-primary" role="status"></div>
                    <div className="mt-1">Compressing image...</div>
                  </div>
                )}

                {/* ✅ Image Preview */}
                {previewImage && previewImage !== 'img/placeholder-img.png' && !isCompressing && (
                  <div className="file-preview text-center mt-2">
                    <img
                      src={previewImage}
                      alt="Preview"
                      className="img-fluid"
                      style={{ maxHeight: "200px", objectFit: "contain" }}
                    />
                  </div>
                )}

                {/* ✅ Upload Progress */}
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="progress mt-2">
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${uploadProgress}%` }}
                    >
                      Uploading... {uploadProgress}%
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger light" onClick={handleClose}>Close</Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isCompressing || uploadProgress > 0}
          >
            {isCompressing
              ? "Processing..."
              : uploadProgress > 0
                ? "Uploading..."
                : "Save changes"}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>


  );
};

export default AddMrvForm;
